import React, {useState, useEffect, useCallback, useReducer, useRef} from 'react';
import styled from 'styled-components';
import Grid from '@material-ui/core/Grid';
import {
  reducer,
  initialState,
} from 'features/SwitchConnectivity/reducers/switchConnectivityReducer';
import useSwitchConnectivity from 'features/SwitchConnectivity/hooks/useSwitchConnectivity';
import SwitchLoadingIndicator from 'features/SwitchConnectivity/components/SwitchLoadingIndicator';
import SwitchSnackbar from 'features/SwitchConnectivity/components/SwitchSnackbar';
import SwitchResponseData from 'features/SwitchConnectivity/interfaces/response/SwitchResponseData';
import PortHistoryResponseData from 'features/SwitchConnectivity/interfaces/response/PortHistoryResponseData';
import SwitchStacksAccordionStateProvider from 'features/SwitchConnectivity/SwitchStack/SwitchStacksAccordionState';
import SwitchStackContainer from 'features/SwitchConnectivity/SwitchStack/SwitchStackContainer';
import SwitchDiagramControls from 'features/SwitchConnectivity/SwitchStack/SwitchDiagramControls';
import FetchResponseError from 'interfaces/FetchResponseError';
import SwitchError from './interfaces/SwitchError';
import SGTitle from 'components/Typography/SGTitle';
import Icon from '@mdi/react';
import {mdiServerNetwork} from '@mdi/js';

//#region styled-components
const Container = styled.div`
  position: relative;
  scroll-margin-top: 16rem;
  scroll-margin-left: 16rem;
  margin-bottom: 32px;
`;

const StyledNoData = styled(Grid)`
  justify-content: center;
  align-items: center;
  margin: 12px;
  gap: 8px;
`;

const StyledIcon = styled(Icon)`
  width: 68px;
  height: 68px;
  color: ${props => props.theme.disabledStateBlue};
`;

//#endregion styled-components

interface SwitchConnectivityProps {
  siteId: string;
  deviceNames: string[];
  updateIntersectedTabs: (hash: string, intersectingRatio: number) => void;
}

function SwitchConnectivity({siteId, deviceNames, updateIntersectedTabs}: SwitchConnectivityProps) {
  const {
    switchData,
    portHistoryData,
    allDataLoaded,
    reloadData,
    error: switchFetchingError,
  } = useSwitchConnectivity(siteId, deviceNames);

  // #region state
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [state, reactDispatch] = useReducer(reducer, initialState);
  const [errors, setErrors] = useState<(FetchResponseError | SwitchError)[]>([]);
  // #endregion state

  // #region functions

  const dispatchUpdateSwitchData = useCallback(
    (data: SwitchResponseData, deviceNames: string[] | undefined) => {
      reactDispatch({
        type: 'UPDATE_SWITCH_DATA',
        payload: {data, deviceNames},
      });
    },
    []
  );

  const dispatchUpdatePortHistoryData = useCallback((data: PortHistoryResponseData[]) => {
    reactDispatch({
      type: 'UPDATE_PORT_HISTORY_DATA',
      payload: data,
    });
  }, []);

  const refresh = useCallback(() => {
    reactDispatch({
      type: 'RESET_SWITCH_CONNECTIVITY',
    });
    reloadData();
  }, []);
  // #endregion functions

  // #region useEffect
  useEffect(() => {
    if (allDataLoaded) {
      if (switchData !== null) {
        dispatchUpdateSwitchData(switchData, deviceNames);
      }
      if (portHistoryData !== null) {
        dispatchUpdatePortHistoryData(portHistoryData);
      }
    }
  }, [
    allDataLoaded,
    switchData,
    portHistoryData,
    dispatchUpdateSwitchData,
    dispatchUpdatePortHistoryData,
    deviceNames,
  ]);

  useEffect(() => {
    if (!allDataLoaded) return;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry =>
          updateIntersectedTabs(entry.target.getAttribute('id')!, entry.intersectionRatio)
        );
      },
      {
        root: null,
        // a bottom margin is added to mirror the navbar height, as both are contained in the same div.
        // if not, switches container takes the whole height of the parent container falling back under the navbar
        rootMargin: '0px 0px 150px 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );

    observer.observe(containerRef?.current!);

    return () => observer.disconnect();
  }, [allDataLoaded]);

  useEffect(() => {
    if (switchData?.errors) setErrors(prevErrors => [...prevErrors, ...switchData?.errors]);
  }, [switchData?.errors]);
  // #endregion useEffect

  return (
    <Container ref={containerRef} id={`${siteId}`} key={siteId ? siteId.toString() : ''}>
      <Grid container spacing={2}>
        <SwitchLoadingIndicator
          key={siteId ? siteId.toString() : ''}
          switchFetchingError={switchFetchingError ? true : false}
          isLoaded={allDataLoaded && state.isSwitchDataReady}
        >
          <SwitchStacksAccordionStateProvider stackNames={state.switchStackNames}>
            <SwitchDiagramControls switchStackNames={state.switchStackNames} />

            {state.switchStackNames.length > 0 ? (
              state.switchStackNames.map(switchStackName => (
                <SwitchStackContainer
                  state={state}
                  dispatch={reactDispatch}
                  switchStackName={switchStackName}
                  refresh={refresh}
                />
              ))
            ) : (
              <StyledNoData container direction="column" spacing={4}>
                <Grid>
                  <StyledIcon path={mdiServerNetwork}></StyledIcon>
                </Grid>
                <Grid>
                  <SGTitle className="text-color" variant="t3">
                    No Data Shown
                  </SGTitle>
                </Grid>
              </StyledNoData>
            )}
          </SwitchStacksAccordionStateProvider>
        </SwitchLoadingIndicator>
      </Grid>

      {errors.map((error, errorIdx) => (
        <SwitchSnackbar
          key={errorIdx}
          error={error}
          onClose={() =>
            setErrors(currentErrors => currentErrors.filter((_, idx) => idx !== errorIdx))
          }
        />
      ))}
    </Container>
  );
}

export default SwitchConnectivity;
